import { useMemo, useState } from 'react';
import SectionHeading from '@/components/feature/SectionHeading';
import { episodes, episodeTags } from '@/mocks/home';

export default function EpisodeList() {
  const [activeTag, setActiveTag] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return episodes.filter((ep) => {
      const tagOk = activeTag === '全部' || ep.tag === activeTag;
      const kwOk =
        kw.length === 0 ||
        ep.title.includes(kw) ||
        ep.summary.includes(kw) ||
        ep.tag.includes(kw) ||
        `第${ep.no}集`.includes(kw);
      return tagOk && kwOk;
    });
  }, [activeTag, keyword]);

  return (
    <section id="episodes" className="w-full bg-background-50 py-14 md:py-20">
      <div className="px-4 md:px-6 max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="Episodes"
          title="全部剧集：按标签挑选你的情绪节奏"
          desc="从全集脉络中筛选出你想看的部分，列表仅作用于本页已有内容，选择标签或输入关键词即可实时过滤。"
        />

        <div className="mt-8 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-1.5 rounded-full bg-background-100 border border-background-200 p-1 w-fit max-w-full">
            {episodeTags.map((tag) => {
              const active = tag === activeTag;
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(tag)}
                  aria-pressed={active}
                  className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-primary-600 text-primary-50'
                      : 'text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  {tag}
                </button>
              );
            })}
          </div>

          <div className="relative max-w-md w-full">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧集标题或关键词，例如“反转”“告白”"
              aria-label="搜索剧集"
              className="w-full pl-9 pr-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>
        </div>

        <p className="mt-4 text-xs md:text-sm text-foreground-500" aria-live="polite">
          共匹配到 <span className="text-primary-600 font-medium">{filtered.length}</span> 个剧集段落
        </p>

        {filtered.length > 0 ? (
          <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {filtered.map((ep) => (
              <li
                key={ep.id}
                className="group rounded-lg border border-background-200 bg-background-100 p-4 md:p-5 flex gap-4 hover:border-primary-300 transition-colors"
              >
                <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-lg bg-primary-600 text-primary-50 font-heading text-lg font-bold">
                  {ep.no}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950">{ep.title}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-label">
                      {ep.tag}
                    </span>
                    {ep.hot ? (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 text-[11px] font-label">
                        <i className="ri-fire-fill" aria-hidden="true" />
                        高能
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">{ep.summary}</p>
                  <p className="mt-2 flex items-center gap-1 text-[11px] md:text-xs text-foreground-400">
                    <i className="ri-time-line" aria-hidden="true" />
                    时长 {ep.duration}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 px-5 py-12 text-center">
            <i className="ri-emotion-unhappy-line text-3xl text-foreground-300" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-600">没有符合条件的剧集，换个标签或关键词试试。</p>
            <button
              type="button"
              onClick={() => {
                setActiveTag('全部');
                setKeyword('');
              }}
              className="mt-4 px-4 py-2 rounded-md bg-primary-600 text-primary-50 text-sm font-label whitespace-nowrap hover:bg-primary-700 transition-colors"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}